import { signIn } from 'next-auth/react';
import { useEffect } from 'react';

import { useMetrics } from '../../../hooks/useMetrics';
import { AUTH_EVENTS, setAuthPending } from '../../../utils/authFunnel';
import MainLoader from '../../Loader';

type NewProvider = 'google' | 'clever' | 'classlink' | 'feide';

type Props = {
  /** Direct-OAuth provider id to redirect through. */
  newProvider: NewProvider;
};

/**
 * Shared SSO self-join entry point (Clever / ClassLink / Feide / Google).
 *
 * Redirects through the direct-OAuth provider. On the callback the NextAuth jwt
 * handler calls /v0.5/auth/{provider}SelfJoin (Basic auth, server-side) which
 * provisions the User + UserIdentity + tenant and syncs the provider's roster —
 * the "self-join" semantics, while still matching existing users.
 */
const SsoSelfJoinRedirect = ({ newProvider }: Props) => {
  const metrics = useMetrics(true);

  useEffect(() => {
    // Funnel: a federated method was chosen via a dedicated SSO entry page. These
    // pages have no mode toggle, so treat them as 'login' intent — isNewUser on
    // the resolved session still reveals signups (incl. signup-via-login).
    metrics.logEvent(AUTH_EVENTS.methodSelected, {
      mode: 'login',
      method: 'oauth',
      provider: newProvider,
      entry: 'ssoPage',
    });
    setAuthPending({ mode: 'login', method: 'oauth', provider: newProvider });
    // Mark this as a provider self-join (roster-sync) flow so the NextAuth
    // callback calls /v0.5/auth/{provider}SelfJoin instead of the generic
    // federated exchange used by the in-form "continue with" tiles.
    document.cookie = 'curipod_auth_flow=selfjoin; path=/; max-age=600; samesite=lax';
    signIn(newProvider, { callbackUrl: '/' });
  }, [newProvider, metrics]);

  return (
    <>
      <MainLoader />
    </>
  );
};

export default SsoSelfJoinRedirect;
